"use server";

import { redirect } from "next/navigation";
import { revalidatePath } from "next/cache";
import { createClient } from "@/lib/supabase/server";
import {
  getAccessibleClientIds,
  requireAdmin,
  requireSession,
  requireStaff,
} from "@/lib/auth/session";
import { normalizeUrl, normalizeName } from "@/lib/utils/normalize";
import { coerceSEOCrawlConfig } from "@/lib/seo-crawl-config";
import { coerceAccessibilityConfig } from "@/lib/accessibility-config";
import { coercePageSpeedAuditConfig } from "@/lib/pagespeed/config";
import type { MaxPages, StrategyConfig } from "@/lib/pagespeed/types";
import {
  parseTemplateConfig,
  parseProspectAuditDepth,
  buildProspectClientPreset,
  buildHostingClientPreset,
} from "@/lib/prospect-setup";
import { DEFAULT_AI_MODEL } from "@/lib/types/database";

const checkboxOn = (formData: FormData, name: string) => formData.getAll(name).includes("on");

async function findDuplicateClient(
  name: string,
  websiteUrl: string,
  excludeId?: string,
): Promise<{ id: string; name: string; field: "name" | "website" } | null> {
  const supabase = await createClient();
  const inputName = normalizeName(name);

  // Check name match server-side (case-insensitive)
  const { data: nameMatches } = await supabase
    .from("clients")
    .select("id, name")
    .ilike("name", inputName)
    .limit(10);

  if (nameMatches) {
    for (const m of nameMatches) {
      if (m.id !== excludeId && normalizeName(m.name) === inputName) {
        return { id: m.id, name: m.name, field: "name" };
      }
    }
  }

  // Check website match if provided
  if (websiteUrl) {
    const inputUrl = normalizeUrl(websiteUrl);
    if (inputUrl) {
      // Narrow to rows that mention this domain instead of scanning every
      // client with a website. normalizeUrl strips protocol/www/trailing
      // slash, so any genuine duplicate's stored URL contains the domain as a
      // substring; the exact normalized comparison below still gates a match,
      // so the ilike only ever over-includes (never misses).
      const domain = inputUrl.split("/")[0];
      const { data: urlCandidates } = await supabase
        .from("clients")
        .select("id, name, website_url")
        .ilike("website_url", `%${domain}%`)
        .limit(100);

      if (urlCandidates) {
        for (const client of urlCandidates) {
          if (client.id === excludeId) continue;
          if (normalizeUrl(client.website_url ?? "") === inputUrl) {
            return { id: client.id, name: client.name, field: "website" };
          }
        }
      }
    }
  }

  return null;
}

export async function createClientAction(formData: FormData) {
  await requireStaff();
  const supabase = await createClient();

  const name = formData.get("name") as string;
  const website_url = formData.get("website_url") as string;

  const duplicate = await findDuplicateClient(name, website_url);
  if (duplicate) {
    redirect(
      `/clients/new?error=duplicate&field=${duplicate.field}&existingId=${duplicate.id}&existingName=${encodeURIComponent(duplicate.name)}`,
    );
  }

  const services = formData.getAll("services").map(String).filter(Boolean);
  const status = (formData.get("status") as string) || "prospect";

  const { data, error } = await supabase.from("clients").insert({
    name,
    website_url,
    industry: (formData.get("industry") as string) || null,
    status,
    services,
  }).select("id").single();

  if (error) throw new Error(error.message);

  // Publishing landing preference. Applied as a separate fail-soft update, the
  // same pattern the tool-settings save uses: the column arrives with the
  // 20260726_publishing_content_items migration, and company creation must
  // succeed before it is applied.
  if (formData.get("publishing_content_client_landing") === "on") {
    const { error: landingErr } = await supabase
      .from("clients")
      .update({ publishing_content_client_landing: true })
      .eq("id", data.id);
    if (landingErr) {
      console.warn(
        "[createClient] publishing_content_client_landing not saved (migration pending?):",
        landingErr.message,
      );
    }
  }

  const template = parseTemplateConfig(formData.get("template_config") as string | null);

  if (template === "prospect") {
    const depth = parseProspectAuditDepth(formData.get("prospect_audit_depth") as string | null);

    // Enable the audits at the chosen depth with manual routines + uptime on.
    const { error: presetErr } = await supabase
      .from("clients")
      .update(buildProspectClientPreset(depth))
      .eq("id", data.id);
    if (presetErr) {
      console.error("[createClient] Failed to apply prospect preset:", presetErr.message);
    }

    // Enable SEO Research (DataForSEO) via its integration row — best effort:
    // never block client creation if this fails (e.g. missing creds).
    const { error: dfsErr } = await supabase
      .from("integrations")
      .insert({ client_id: data.id, provider: "dataforseo", credentials: {}, status: "connected" });
    if (dfsErr) {
      console.error("[createClient] Failed to enable SEO Research:", dfsErr.message);
    }

    redirect(`/clients/${data.id}/setup?prospect_init=${depth}`);
  }

  if (template === "hosting") {
    // Hosting: the five tools on manual routines (uptime on) with app-default
    // audit configs. No SEO Research, no one-time auto-run.
    const { error: presetErr } = await supabase
      .from("clients")
      .update(buildHostingClientPreset())
      .eq("id", data.id);
    if (presetErr) {
      console.error("[createClient] Failed to apply hosting preset:", presetErr.message);
    }
  }

  redirect(`/clients/${data.id}/setup`);
}

export async function updateClientAction(
  _prevState: { error?: string; field?: string; existingId?: string; existingName?: string } | null,
  formData: FormData,
) {
  await requireStaff();
  const supabase = await createClient();
  const id = formData.get("id") as string;
  const name = formData.get("name") as string;
  const website_url = formData.get("website_url") as string;

  const duplicate = await findDuplicateClient(name, website_url, id);
  if (duplicate) {
    return {
      error: "duplicate",
      field: duplicate.field,
      existingId: duplicate.id,
      existingName: duplicate.name,
    };
  }

  const services = formData.getAll("services").map(String).filter(Boolean);

  const { error } = await supabase
    .from("clients")
    .update({
      name,
      website_url,
      industry: (formData.get("industry") as string) || null,
      status: formData.get("status") as string,
      services,
      slack_channel_id: (formData.get("slack_channel_id") as string) || null,
    })
    .eq("id", id);

  if (error) return { error: error.message };

  redirect(`/clients/${id}/setup?saved=details`);
}

export async function saveToolSettingsAction(formData: FormData): Promise<{ success: boolean; error?: string }> {
  await requireStaff();
  const supabase = await createClient();
  const id = formData.get("id") as string;

  const { error } = await supabase
    .from("clients")
    .update({
      uptime_monitoring: formData.get("uptime_monitoring") === "on",
      uptime_up_policy: formData.get("uptime_up_policy") === "on" ? "reachable" : "strict",
      uptime_paused: formData.get("uptime_paused") === "on",
      cms_error_monitoring: (formData.get("cms_error_monitoring") as string) || null,
      scheduled_reports_enabled: formData.get("scheduled_reports_enabled") === "on",
      audit_schedule_site_audit: (formData.get("audit_schedule_site_audit") as string) || "none",
      audit_schedule_seo_crawl: (formData.get("audit_schedule_seo_crawl") as string) || "none",
      audit_schedule_accessibility: (formData.get("audit_schedule_accessibility") as string) || "none",
      audit_schedule_pagespeed: (formData.get("audit_schedule_pagespeed") as string) || "none",
      audit_schedule_ai_search_health: (formData.get("audit_schedule_ai_search_health") as string) || "none",
      pagespeed_config: coercePageSpeedAuditConfig({
        strategy: (formData.get("pagespeed_strategy") as StrategyConfig | null) ?? undefined,
        maxPages: (Number(formData.get("pagespeed_max_pages")) || undefined) as MaxPages | undefined,
        timeout: Number(formData.get("pagespeed_timeout")) || undefined,
        respectRobotsTxt: checkboxOn(formData, "pagespeed_respectRobotsTxt"),
        includeNonSitemapPages: checkboxOn(formData, "pagespeed_includeNonSitemapPages"),
      }),
      seo_crawl_config: coerceSEOCrawlConfig({
        maxPages: Number(formData.get("seo_crawl_maxPages")) || undefined,
        maxDepth: Number(formData.get("seo_crawl_maxDepth")) || undefined,
        concurrency: Number(formData.get("seo_crawl_concurrency")) || undefined,
        timeout: Number(formData.get("seo_crawl_timeout")) || undefined,
        respectRobotsTxt: checkboxOn(formData, "seo_crawl_respectRobotsTxt"),
        includeNonSitemapPages: checkboxOn(formData, "seo_crawl_includeNonSitemapPages"),
      }),
      accessibility_config: coerceAccessibilityConfig({
        maxPages: Number(formData.get("accessibility_maxPages")) || undefined,
        concurrency: Number(formData.get("accessibility_concurrency")) || undefined,
        timeout: Number(formData.get("accessibility_timeout")) || undefined,
        respectRobotsTxt: checkboxOn(formData, "accessibility_respectRobotsTxt"),
        includeNonSitemapPages: checkboxOn(formData, "accessibility_includeNonSitemapPages"),
      }),
      keyword_tracker_enabled: formData.get("keyword_tracker_enabled") === "on",
      ai_recommendations_enabled: formData.get("ai_recommendations_enabled") === "on",
      // Dependent toggle: Deep Research cannot be on without AI Recommendations
      // (the form disables it, and this re-enforces it server-side).
      deep_research_enabled:
        formData.get("deep_research_enabled") === "on" &&
        formData.get("ai_recommendations_enabled") === "on",
      // All AI generation is locked to the default model (Sonnet); the form no
      // longer offers a choice, and saving normalizes any older stored value.
      ai_recommendations_model: DEFAULT_AI_MODEL,
    })
    .eq("id", id);

  if (error) return { success: false, error: error.message };

  // Social Publishing toggle. Separate fail-soft update: the column arrives
  // with the 20260712 migration and the rest of the save must work before it.
  const { error: socialError } = await supabase
    .from("clients")
    .update({ social_publishing_enabled: formData.get("social_publishing_enabled") === "on" })
    .eq("id", id);
  if (socialError) {
    console.warn("[tool settings] social_publishing_enabled not saved (migration pending?):", socialError.message);
  }

  // Competitor Profiles toggle. Fail-soft (column arrives with the
  // 20260730_competitor_profiles migration) and dependent on a connected
  // DataForSEO integration; the form disables it without one, and this
  // re-enforces it server-side like the Deep Research dependency.
  let competitorProfilesOn = formData.get("competitor_profiles_enabled") === "on";
  if (competitorProfilesOn) {
    const { data: dfsRow } = await supabase
      .from("integrations")
      .select("id")
      .eq("client_id", id)
      .eq("provider", "dataforseo")
      .maybeSingle();
    competitorProfilesOn = Boolean(dfsRow);
  }
  const { error: profilesError } = await supabase
    .from("clients")
    .update({ competitor_profiles_enabled: competitorProfilesOn })
    .eq("id", id);
  if (profilesError) {
    console.warn("[tool settings] competitor_profiles_enabled not saved (migration pending?):", profilesError.message);
  }

  // AEO Research toggle. Same fail-soft pattern (column arrives with the
  // 20260731_aeo_research migration) and the same server-side DataForSEO
  // dependency re-check as Competitor Profiles.
  let aeoResearchOn = formData.get("aeo_research_enabled") === "on";
  if (aeoResearchOn) {
    const { data: dfsRowForAeo } = await supabase
      .from("integrations")
      .select("id")
      .eq("client_id", id)
      .eq("provider", "dataforseo")
      .maybeSingle();
    aeoResearchOn = Boolean(dfsRowForAeo);
  }
  const { error: aeoError } = await supabase
    .from("clients")
    .update({ aeo_research_enabled: aeoResearchOn })
    .eq("id", id);
  if (aeoError) {
    console.warn("[tool settings] aeo_research_enabled not saved (migration pending?):", aeoError.message);
  }

  // Sitemap Planner toggle. Same fail-soft pattern: the column arrives with
  // the 20260711_sitemap_planner migration.
  const { error: sitemapError } = await supabase
    .from("clients")
    .update({ sitemap_planner_enabled: formData.get("sitemap_planner_enabled") === "on" })
    .eq("id", id);
  if (sitemapError) {
    console.warn("[tool settings] sitemap_planner_enabled not saved (migration pending?):", sitemapError.message);
  }

  // Content-list landing for client users. Same fail-soft pattern: the column
  // arrives with the 20260726_publishing_content_items migration.
  const { error: landingError } = await supabase
    .from("clients")
    .update({
      publishing_content_client_landing:
        formData.get("publishing_content_client_landing") === "on",
    })
    .eq("id", id);
  if (landingError) {
    console.warn(
      "[tool settings] publishing_content_client_landing not saved (migration pending?):",
      landingError.message,
    );
  }

  // Sync uptime_monitoring to linked website
  const uptimeValue = formData.get("uptime_monitoring") === "on";
  const { syncClientToWebsite } = await import("@/lib/actions/websites");
  await syncClientToWebsite(id, "uptime_monitoring", uptimeValue);

  // SEO Research (DataForSEO) is enabled by the existence of its integration row.
  // Sync that row to the Setup toggle: create on enable, remove on disable.
  const dataforseoEnabled = formData.get("dataforseo_enabled") === "on";
  const { data: existingDataForSEO, error: selErr } = await supabase
    .from("integrations")
    .select("id")
    .eq("client_id", id)
    .eq("provider", "dataforseo")
    .maybeSingle();
  if (selErr) return { success: false, error: selErr.message };

  if (dataforseoEnabled && !existingDataForSEO) {
    const { error: insErr } = await supabase
      .from("integrations")
      .insert({ client_id: id, provider: "dataforseo", credentials: {}, status: "connected" });
    if (insErr) return { success: false, error: insErr.message };
  } else if (!dataforseoEnabled && existingDataForSEO) {
    const { error: delErr } = await supabase
      .from("integrations")
      .delete()
      .eq("id", existingDataForSEO.id);
    if (delErr) return { success: false, error: delErr.message };
  }

  // Refresh the client layout so newly enabled tools surface in the sidebar nav
  // and dashboard widgets immediately, without waiting for the first audit run.
  revalidatePath(`/clients/${id}`, "layout");

  return { success: true };
}

export async function setUptimePausedAction(
  clientId: string,
  paused: boolean
): Promise<{ success: boolean; error?: string }> {
  await requireStaff();
  const supabase = await createClient();
  const { error } = await supabase
    .from("clients")
    .update({ uptime_paused: paused })
    .eq("id", clientId);
  if (error) return { success: false, error: error.message };
  revalidatePath(`/clients/${clientId}/dashboard`);
  revalidatePath("/agency/uptime");
  return { success: true };
}

export async function deleteClientAction(formData: FormData) {
  await requireAdmin();
  const supabase = await createClient();
  const id = formData.get("id") as string;

  const { error } = await supabase.from("clients").delete().eq("id", id);

  if (error) throw new Error(error.message);

  redirect("/");
}

export async function fetchClientListAction(): Promise<
  { id: string; name: string; status: string }[]
> {
  const session = await requireSession();
  const supabase = await createClient();
  const accessibleClientIds = await getAccessibleClientIds(session);
  if (accessibleClientIds === null) {
    const { data } = await supabase
      .from("clients")
      .select("id, name, status")
      .order("status")
      .order("name");
    return (data ?? []) as { id: string; name: string; status: string }[];
  }

  if (accessibleClientIds.length === 0) return [];
  const { data } = await supabase
    .from("clients")
    .select("id, name, status")
    .in("id", accessibleClientIds)
    .order("status")
    .order("name");

  return (data ?? []) as { id: string; name: string; status: string }[];
}
